import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { contractService } from '../services/contractService';
import { pdfService } from '../services/pdfService';
import { Contract } from '../types/contract';
import { Download, AlertCircle, CheckCircle, Loader2 } from 'lucide-react';

const ContractPDFDownload: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [contract, setContract] = useState<Contract | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [downloading, setDownloading] = useState(false);
  const hasDownloadedRef = useRef(false);

  useEffect(() => {
    const loadContract = async () => {
      if (!id) {
        setError('ID do contrato não fornecido');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        
        // Buscar contrato pelo ID
        const contractData = await contractService.getContractById(id);
        
        if (!contractData) {
          setError('Contrato não encontrado');
          setLoading(false);
          return;
        }

        setContract(contractData);
        setLoading(false);
        
      } catch (err) {
        console.error('Erro ao carregar contrato:', err);
        setError('Erro ao carregar contrato. Verifique se o ID está correto.');
        setLoading(false);
      }
    };

    loadContract();
  }, [id]);

  const generateAndDownloadPDF = async (contractData: Contract) => {
    if (hasDownloadedRef.current) {
      return; // Já foi baixado, não executa novamente
    }

    try {
      setDownloading(true);
      hasDownloadedRef.current = true; // Marca como baixado imediatamente
      
      // Gerar PDF usando o novo serviço que replica o layout da página de detalhes
      const pdfBlob = await pdfService.generateContractPDF(contractData);
      
      // Criar URL para download
      const url = URL.createObjectURL(pdfBlob);
      
      // Criar link para download
      const link = document.createElement('a');
      link.href = url;
      link.download = `contrato_${contractData.contratante}_${contractData.dataFesta}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Limpar URL
      URL.revokeObjectURL(url);
      
      setDownloading(false);
      
    } catch (err) {
      console.error('Erro ao gerar PDF:', err);
      setError('Erro ao gerar PDF do contrato');
      setDownloading(false);
      hasDownloadedRef.current = false; // Permite tentar novamente em caso de erro
    }
  };

  const handleRetry = () => {
    if (contract) {
      hasDownloadedRef.current = false; // Reset para permitir novo download
      generateAndDownloadPDF(contract);
    }
  };

  const handleGoHome = () => {
    navigate('/');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 flex items-center justify-center">
        <div className="bg-white rounded-2xl p-8 shadow-xl max-w-md w-full mx-4">
          <div className="text-center">
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
            </div>
            <h2 className="text-xl font-semibold text-slate-900 mb-2">
              Carregando Contrato
            </h2>
            <p className="text-slate-600">
              Buscando dados do contrato...
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-red-50 to-pink-50 flex items-center justify-center">
        <div className="bg-white rounded-2xl p-8 shadow-xl max-w-md w-full mx-4">
          <div className="text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertCircle className="w-8 h-8 text-red-600" />
            </div>
            <h2 className="text-xl font-semibold text-slate-900 mb-2">
              Erro ao Carregar
            </h2>
            <p className="text-slate-600 mb-6">
              {error}
            </p>
            <div className="space-y-3">
              <button
                onClick={handleRetry}
                className="w-full bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors"
              >
                Tentar Novamente
              </button>
              <button
                onClick={handleGoHome}
                className="w-full bg-slate-200 text-slate-700 px-6 py-3 rounded-lg hover:bg-slate-300 transition-colors"
              >
                Voltar ao Início
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl max-w-2xl w-full">
        {/* Header com Logo */}
        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 rounded-t-2xl p-6 text-center">
          <img 
            src="/images/LOGO.png" 
            alt="Play Kids Logo" 
            className="h-16 mx-auto mb-4"
          />
          <h1 className="text-2xl font-bold text-white">
            Download do Contrato
          </h1>
          <p className="text-blue-100 mt-2">
            Sistema de Contratos Play Kids
          </p>
        </div>

        <div className="p-8">
          {downloading ? (
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
              </div>
              <h2 className="text-xl font-semibold text-slate-900 mb-2">
                Gerando PDF
              </h2>
              <p className="text-slate-600">
                Preparando download do contrato...
              </p>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Dados do Contrato */}
              <div className="bg-slate-50 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                  <div className="w-2 h-2 bg-blue-600 rounded-full"></div>
                  Dados do Contrato
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="font-medium text-slate-600">Contratante:</span>
                    <p className="text-slate-900 font-semibold">{contract?.contratante}</p>
                  </div>
                  <div>
                    <span className="font-medium text-slate-600">Data da Festa:</span>
                    <p className="text-slate-900 font-semibold">
                      {contract?.dataFesta ? (() => {
                        try {
                          const date = new Date(contract.dataFesta);
                          return isNaN(date.getTime()) ? contract.dataFesta : date.toLocaleDateString('pt-BR');
                        } catch {
                          return contract.dataFesta;
                        }
                      })() : 'N/A'}
                    </p>
                  </div>
                  <div>
                    <span className="font-medium text-slate-600">Horário:</span>
                    <p className="text-slate-900 font-semibold">
                      {contract?.inicioRecreacao} às {contract?.fimRecreacao}
                    </p>
                  </div>
                  <div>
                    <span className="font-medium text-slate-600">Valor Total:</span>
                    <p className="text-slate-900 font-semibold text-green-600">
                      R$ {contract?.valor ? parseFloat(contract.valor).toLocaleString('pt-BR', { minimumFractionDigits: 2 }) : '0,00'}
                    </p>
                  </div>
                  <div>
                    <span className="font-medium text-slate-600">Cidade:</span>
                    <p className="text-slate-900 font-semibold">{contract?.cidade}</p>
                  </div>
                  <div>
                    <span className="font-medium text-slate-600">Tema:</span>
                    <p className="text-slate-900 font-semibold">{contract?.temaFesta}</p>
                  </div>
                </div>
              </div>

              {/* Botões de Ação */}
              <div className="text-center space-y-3">
                <button
                  onClick={() => contract && generateAndDownloadPDF(contract)}
                  className="w-full bg-blue-600 text-white px-6 py-4 rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center gap-3 text-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
                >
                  <Download className="w-6 h-6" />
                  Baixar PDF do Contrato
                </button>
                <button
                  onClick={handleGoHome}
                  className="w-full bg-slate-200 text-slate-700 px-6 py-3 rounded-lg hover:bg-slate-300 transition-colors font-medium"
                >
                  Voltar ao Início
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ContractPDFDownload;
